// client/src/v2/overlay-story.tsx
//
// Overlay para Stories — MONTADO, não capturado.
//
// O `share-card.tsx` continua intacto: ele tira print do DOM. Aqui é outro
// caminho, do zero. A peça é DESENHADA em canvas com fundo transparente — do
// jeito que a Strava faz: o leitor fotografa o livro que acabou de fechar e
// cola este selo por cima. A foto é dele; o adesivo só carimba o resultado.
//
// Duas consequências de projeto que vêm daí:
//
//   1. FUNDO TRANSPARENTE exige PNG. JPG não tem canal alfa — exportar em JPG
//      preencheria tudo de preto e a peça deixaria de ser overlay.
//
//   2. A peça é MONOCROMÁTICA. Como ela vai cair sobre uma foto que ninguém
//      controla, não dá pra fixar cor: quem escolhe é o leitor. Desenhar tudo
//      numa cor só (variando apenas a opacidade) é o que faz um seletor de
//      cor resolver a legibilidade inteira — é por isso que a Strava também
//      desenha monocromático.
//
// Nada aqui lê o DOM. Os números chegam por prop e viram pixel.

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './icons';
import { isNativeApp } from './platform';

// ─── Medidas do adesivo ───────────────────────────────────────────────────
//
// O adesivo NÃO é o story. A foto é do leitor — ele fotografa o livro que
// acabou de fechar e cola isto por cima, do jeito que se cola o resultado do
// treino no Strava. Por isso o arquivo sai no tamanho do SELO, não no do
// quadro 1080×1920: um PNG do tamanho do story sairia com uma tarja de
// conteúdo perdida no meio de um oceano transparente, impossível de
// posicionar e de escalar.
//
// Largura fixa, altura calculada a partir do conteúdo — título de três
// linhas não pode vazar nem deixar buraco.
const LARG = 900;
const PAD = 46;

export type DadosOverlay =
  | {
      tipo: 'meta';
      paginasHoje: number;
      metaHoje: number;
      unidadeLonga: string;   // "páginas", "minutos", "capítulos"
      chama: number;          // dias consecutivos
      metaBatida: boolean;
    }
  | {
      tipo: 'livro';
      titulo: string;
      autor?: string | null;
      lidoHoje: number;        // quanto rendeu HOJE neste livro
      metaDoLivro: number;     // a meta diária deste livro (0 = sem meta)
      metaBatida: boolean;     // bateu a meta do dia NESTE livro
      paginaAtual: number;
      totalPaginas: number;
      unidadeLonga: string;
      capaUrl?: string | null;
      terminado?: boolean;     // o livro ACABOU — não confundir com meta do dia
    }
  | {
      // O progresso no Protocolo do Hábito: estar no meio de uma coisa longa.
      tipo: 'protocolo';
      dia: number;             // dia do programa
      marco: number;           // o marco (67) — só vira texto enquanto for verdade
      estagio: string;         // "A Ignição", "A Identidade"…
      chama: number;           // dias seguidos
      diasLidos: number;
      formado: boolean;        // passou o marco e o programa cumpriu a função
      titulo?: string | null;  // o livro do protocolo — opcional, como o topo
      autor?: string | null;
      capaUrl?: string | null;
    }
  | {
      tipo: 'nota';
      titulo: string;          // o LIVRO da nota
      autor?: string | null;
      capaUrl?: string | null;
      texto: string;           // o grifo, a ideia, a pergunta
      rotulo: string;          // GRIFO · IDEIA · NOTA · PERGUNTA
      pagina?: string | null;  // "p. 138"
    };

// ─── Cores oferecidas ─────────────────────────────────────────────────────
// Branco e preto resolvem quase toda foto; o resto é a paleta da marca, pra
// quem quiser o overlay combinando com o próprio feed.
const CORES: { nome: string; hex: string }[] = [
  { nome: 'Branco', hex: '#FFFFFF' },
  { nome: 'Preto',  hex: '#161210' },
  { nome: 'Brasa',  hex: '#E87A3B' },
  { nome: 'Creme',  hex: '#F3ECDE' },
  { nome: 'Sálvia', hex: '#9EB083' },
  { nome: 'Ameixa', hex: '#C08CAE' },
];

// ─── Fundos de card ───────────────────────────────────────────────────────
// O padrão continua sendo SEM fundo — o adesivo transparente é o que dá o
// gesto do Strava. Estes são para quem quer a peça fechada, com a cara dos
// cards do app. `texto` é a cor que se lê em cima de cada um.
const FUNDOS: { nome: string; hex: string | null; texto: string | null }[] = [
  { nome: 'Sem',    hex: null,      texto: null },
  { nome: 'Marrom', hex: '#1F1A17', texto: '#F3ECDE' },
  { nome: 'Branco', hex: '#FFFFFF', texto: '#161210' },
  { nome: 'Preto',  hex: '#0E0C0B', texto: '#F3ECDE' },
];

// ─── Desenho ──────────────────────────────────────────────────────────────

/** Contorno do texto. `larg: 0` = desligado. */
export interface Contorno { cor: string; larg: number }

/**
 * Escreve com contorno opcional.
 *
 * O traço vai ANTES do preenchimento e com o dobro da largura pedida: o
 * `strokeText` desenha centrado no contorno da letra, então metade cai por
 * dentro e some sob o `fillText`. Sem dobrar, o contorno sai pela metade do
 * que a pessoa escolheu.
 */
function tracar(
  ctx: CanvasRenderingContext2D, texto: string, x: number, y: number, ct: Contorno,
): void {
  ctx.save();
  ctx.strokeStyle = ct.cor;
  ctx.lineWidth = ct.larg * 2;
  ctx.lineJoin = 'round';
  ctx.miterLimit = 2;
  // Contorno fino tem que sair NÍTIDO, não esfumaçado. A sombra herdada
  // borrava o traço e o resultado parecia fumaça em volta da letra.
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.strokeText(texto, x, y);
  ctx.restore();
}

function escrever(
  ctx: CanvasRenderingContext2D, texto: string, x: number, y: number, ct?: Contorno,
): void {
  if (ct && ct.larg > 0) tracar(ctx, texto, x, y, ct);
  ctx.fillText(texto, x, y);
}

/**
 * Texto com espaçamento entre letras, caractere por caractere.
 *
 * `ctx.letterSpacing` só existe em navegador recente e o app roda dentro de
 * WKWebView — desenhar na mão é chato mas nunca falha, e o tracking largo é
 * metade da assinatura tipográfica da marca nos rótulos.
 */
function textoEspacado(
  ctx: CanvasRenderingContext2D, texto: string, x: number, y: number, espaco: number,
  ct?: Contorno,
): number {
  // Dois passes de propósito: TODO o contorno primeiro, só então o
  // preenchimento. Alternando letra a letra, o traço da letra seguinte
  // mordia o preenchimento da anterior e a borda saía suja.
  if (ct && ct.larg > 0) {
    let c = x;
    for (const ch of texto) { tracar(ctx, ch, c, y, ct); c += ctx.measureText(ch).width + espaco; }
  }
  let cursor = x;
  for (const ch of texto) {
    ctx.fillText(ch, cursor, y);
    cursor += ctx.measureText(ch).width + espaco;
  }
  return cursor - x;
}

/**
 * Chama em bézier, monocromática igual ao resto.
 *
 * A primeira versão era simétrica e saía uma gota. O que faz o olho ler
 * "fogo" é a assimetria: ponta puxada pra um lado e uma dobra na lateral
 * esquerda, em vez de duas curvas espelhadas.
 */
function chamaPath(cx: number, cy: number, s: number): Path2D {
  const p = new Path2D();
  p.moveTo(cx + s * 0.06, cy - s);
  p.bezierCurveTo(cx + s * 0.60, cy - s * 0.40, cx + s * 0.74, cy + s * 0.14, cx + s * 0.44, cy + s * 0.60);
  p.bezierCurveTo(cx + s * 0.20, cy + s * 0.96, cx - s * 0.30, cy + s * 0.96, cx - s * 0.48, cy + s * 0.54);
  p.bezierCurveTo(cx - s * 0.70, cy + s * 0.06, cx - s * 0.26, cy - s * 0.06, cx - s * 0.16, cy - s * 0.44);
  p.bezierCurveTo(cx - s * 0.10, cy - s * 0.68, cx - s * 0.04, cy - s * 0.86, cx + s * 0.06, cy - s);
  p.closePath();
  return p;
}

/**
 * Marca de meta cumprida.
 *
 * Era círculo com check dentro e ficava pesado ao lado do número — dois
 * desenhos disputando o mesmo canto. Agora é só o traço do check, que lê
 * como sinal e não como carimbo.
 */
function marcaCheck(ctx: CanvasRenderingContext2D, x: number, cy: number, s: number, cor: string): void {
  ctx.save();
  ctx.globalAlpha = 0.95;
  ctx.strokeStyle = cor;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.lineWidth = s * 0.24;
  ctx.shadowColor = 'transparent';
  ctx.beginPath();
  ctx.moveTo(x, cy + s * 0.04);
  ctx.lineTo(x + s * 0.34, cy + s * 0.42);
  ctx.lineTo(x + s, cy - s * 0.46);
  ctx.stroke();
  ctx.restore();
}

/** Claro ou escuro? Decide a direção da sombra de legibilidade. */
function ehClara(hex: string): boolean {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.6;
}

async function garantirFontes(): Promise<void> {
  try {
    await Promise.all([
      (document as any).fonts?.load?.('400 250px "Instrument Serif"'),
      (document as any).fonts?.load?.('italic 400 62px "Instrument Serif"'),
      (document as any).fonts?.load?.('600 34px "Geist Mono"'),
    ]);
    await (document as any).fonts?.ready;
  } catch { /* fonte é enfeite: se falhar, o fallback desenha do mesmo jeito */ }
}

/**
 * Carrega a capa pro canvas.
 *
 * `crossOrigin='anonymous'` é o detalhe que importa: no caso normal a capa
 * vive em /uploads/covers (mesma origem) e nada muda; no caso raro de capa
 * externa sem CORS, o onerror devolve null e a peça é desenhada SEM capa —
 * em vez de contaminar o canvas e fazer o `toBlob` estourar depois. Era
 * exatamente aí que o caminho antigo se perdia e "não pegava a capa".
 */
function carregarCapa(url: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    let respondido = false;
    const responder = (v: HTMLImageElement | null) => { if (!respondido) { respondido = true; resolve(v); } };
    img.crossOrigin = 'anonymous';
    img.onload = () => responder(img);
    img.onerror = () => responder(null);
    img.src = url;
    setTimeout(() => responder(null), 5000);
  });
}

/**
 * A logo oficial, carregada uma vez e guardada.
 *
 * Ela é o único elemento que NÃO é monocromático — e é de propósito: marca
 * não muda de cor conforme a foto de quem posta. Mora em /icons (mesma
 * origem), então não contamina o canvas.
 */
let _logo: HTMLImageElement | null = null;
async function garantirLogo(): Promise<void> {
  if (_logo) return;
  _logo = await carregarCapa('/icons/forjamente-icon.png');
}

/** Assinatura do rodapé: logo + endereço. Nunca sai uma sem a outra. */
function assinatura(ctx: CanvasRenderingContext2D, est: Estilo, x: number, base: number): void {
  const L = 36;
  let cursor = x;
  if (_logo) {
    ctx.save();
    ctx.globalAlpha = 1;
    ctx.shadowColor = 'transparent';
    ctx.drawImage(_logo, x, base - L + 7, L, L);
    ctx.restore();
    cursor += L + 13;
  }
  ctx.globalAlpha = 0.42;
  ctx.fillStyle = est.cor;
  ctx.font = F.marca;
  textoEspacado(ctx, 'FORJAMENTE.COM.BR', cursor, base, 6, est.ct);
}

/** Retângulo de cantos arredondados — a capa e o berço dela usam. */
function caminhoArredondado(x: number, y: number, w: number, h: number, r: number): Path2D {
  const p = new Path2D();
  p.moveTo(x + r, y);
  p.arcTo(x + w, y, x + w, y + h, r);
  p.arcTo(x + w, y + h, x, y + h, r);
  p.arcTo(x, y + h, x, y, r);
  p.arcTo(x, y, x + w, y, r);
  p.closePath();
  return p;
}

/** Quebra o título em linhas que cabem na largura. */
function quebrar(ctx: CanvasRenderingContext2D, texto: string, largura: number, maxLinhas: number): string[] {
  const palavras = String(texto || '').split(/\s+/);
  const linhas: string[] = [];
  let atual = '';
  for (const p of palavras) {
    const tentativa = atual ? atual + ' ' + p : p;
    if (ctx.measureText(tentativa).width > largura && atual) { linhas.push(atual); atual = p; }
    else atual = tentativa;
    if (linhas.length === maxLinhas) break;
  }
  if (linhas.length < maxLinhas && atual) linhas.push(atual);
  if (linhas.length === maxLinhas) {
    let ultima = linhas[maxLinhas - 1];
    while (ctx.measureText(ultima + '…').width > largura && ultima.length > 4) ultima = ultima.slice(0, -1);
    if (palavras.join(' ') !== linhas.join(' ')) linhas[maxLinhas - 1] = ultima.trimEnd() + '…';
  }
  return linhas;
}

/**
 * Tudo que o leitor escolhe sobre a aparência da peça.
 *
 * Virou um objeto porque já eram cinco decisões independentes viajando como
 * parâmetro solto por seis funções — a próxima quebraria alguma assinatura.
 */
export interface Estilo {
  cor: string;
  ct?: Contorno;
  /** Fundo do card. `null` = adesivo transparente, como sempre foi. */
  fundo?: string | null;
  /** Capa + título + autor entram? Se não, sobra só o conteúdo. */
  comTopo: boolean;
}

/** Prepara o contexto do jeito que as peças precisam. */
function preparar(ctx: CanvasRenderingContext2D, est: Estilo) {
  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = 'left';
  // Sombra suave na direção contrária à cor do texto — é o que segura a
  // legibilidade quando a foto por baixo tem o mesmo tom da letra. Com
  // contorno ligado ela sai de cena: as duas juntas embolam a letra. Com
  // fundo de card ela também não faz falta — o fundo já separa do mundo.
  const dispensa = (!!est.ct && est.ct.larg > 0) || !!est.fundo;
  ctx.shadowColor = dispensa
    ? 'transparent'
    : (ehClara(est.cor) ? 'rgba(0,0,0,0.38)' : 'rgba(255,255,255,0.34)');
  ctx.shadowBlur = dispensa ? 0 : 20;
  ctx.shadowOffsetY = dispensa ? 0 : 2;
  return (alpha: number) => { ctx.globalAlpha = alpha; ctx.fillStyle = est.cor; };
}

/** Contexto descartável só para medir texto antes de existir canvas final. */
function medidor(): CanvasRenderingContext2D {
  const cv = document.createElement('canvas');
  cv.width = LARG; cv.height = 10;
  return cv.getContext('2d') as CanvasRenderingContext2D;
}

// ─── Fontes do adesivo ────────────────────────────────────────────────────
// A hierarquia veio do Wesley: o livro, o quanto rendeu hoje e o tamanho do
// livro são os heróis. Rótulo, autor e marca são legenda deles.
const F = {
  rotulo:  '600 29px "Geist Mono", "SF Mono", ui-monospace, monospace',
  titulo:  '400 68px "Instrument Serif", Georgia, serif',
  autor:   'italic 400 36px "Instrument Serif", Georgia, serif',
  heroi:   '400 118px "Instrument Serif", Georgia, serif',
  legenda: '600 24px "Geist Mono", "SF Mono", ui-monospace, monospace',
  marca:   '600 21px "Geist Mono", "SF Mono", ui-monospace, monospace',
  nota:    'italic 400 48px "Instrument Serif", Georgia, serif',
  // O segundo herói entra menor que o primeiro, e a régua (/430) menor
  // ainda: quem leu hoje é o feito; o tamanho do livro é só a medida.
  heroi2:  '400 88px "Instrument Serif", Georgia, serif',
  sufixo:  '400 54px "Instrument Serif", Georgia, serif',
};

// Capa e título crescem juntos até quase encostar na borda — o bloco de cima
// estava pequeno demais para o peso que tem. Os números não mudam: já estão
// no tamanho certo.
const CAPA_W = 152, CAPA_H = 228;

// Distância do bloco de cima até o fio, e do fio até o conteúdo. Eram 40 e
// 44 e a peça lia como duas peças soltas — agora o conjunto respira junto.
const ATE_FIO = 22;
const DEPOIS_FIO = 30;

/**
 * O bloco de cima — capa, rótulo, título, autor.
 *
 * É o mesmo nas duas peças que falam de um livro (a da leitura e a da nota),
 * então mora aqui em vez de existir duplicado com duas contas de altura
 * que iriam divergir na primeira mudança.
 */
function planoTopo(titulo: string, autor?: string | null) {
  const ctx = medidor();
  const colTexto = PAD + CAPA_W + 30;
  ctx.font = F.titulo;
  const linhas = quebrar(ctx, titulo, LARG - colTexto - PAD, 3);
  const alturaTexto = 40 + linhas.length * 74 + (autor ? 50 : 0);
  return { colTexto, linhas, alturaTopo: Math.max(CAPA_H, alturaTexto) };
}

function desenharTopo(
  ctx: CanvasRenderingContext2D, est: Estilo, capa: HTMLImageElement | null,
  rotulo: string, titulo: string, autor: string | null | undefined,
): number {
  const tinta = (alpha: number) => { ctx.globalAlpha = alpha; ctx.fillStyle = est.cor; };
  const ct = est.ct;
  const { colTexto, linhas, alturaTopo } = planoTopo(titulo, autor);
  const capaY = PAD + (alturaTopo - CAPA_H) / 2;

  // ── capa ──
  const berco = caminhoArredondado(PAD, capaY, CAPA_W, CAPA_H, 8);
  ctx.save();
  ctx.shadowColor = 'rgba(0,0,0,0.5)';
  ctx.shadowBlur = 22;
  ctx.shadowOffsetY = 8;
  if (capa) {
    ctx.clip(berco);
    const escala = Math.max(CAPA_W / capa.naturalWidth, CAPA_H / capa.naturalHeight);
    const lw = capa.naturalWidth * escala, lh = capa.naturalHeight * escala;
    ctx.drawImage(capa, PAD + (CAPA_W - lw) / 2, capaY + (CAPA_H - lh) / 2, lw, lh);
  } else {
    // Sem capa: uma lombada desenhada, que é melhor que um buraco.
    ctx.globalAlpha = 0.18; ctx.fillStyle = est.cor; ctx.fill(berco);
    ctx.shadowBlur = 0;
    ctx.globalAlpha = 0.45; ctx.strokeStyle = est.cor; ctx.lineWidth = 2.5; ctx.stroke(berco);
    ctx.fillRect(PAD + 15, capaY, 2.5, CAPA_H);
  }
  ctx.restore();
  preparar(ctx, est);

  // ── identidade ──
  let y = PAD + (alturaTopo - (40 + linhas.length * 74 + (autor ? 50 : 0))) / 2;
  tinta(0.68);
  ctx.font = F.rotulo;
  textoEspacado(ctx, rotulo, colTexto, y + 24, 8, ct);
  y += 40;

  tinta(1);
  ctx.font = F.titulo;
  for (const linha of linhas) { escrever(ctx, linha, colTexto, y + 54, ct); y += 74; }

  if (autor) {
    tinta(0.62);
    ctx.font = F.autor;
    escrever(ctx, quebrar(ctx, autor, LARG - colTexto - PAD, 1)[0] || '', colTexto, y + 32, ct);
  }

  // devolve o topo do fio, logo abaixo do conjunto
  return PAD + alturaTopo + ATE_FIO;
}

/** Só o rótulo, quando a pessoa dispensa capa e título. */
function desenharRotuloSolto(ctx: CanvasRenderingContext2D, est: Estilo, rotulo: string): number {
  ctx.globalAlpha = 0.68; ctx.fillStyle = est.cor;
  ctx.font = F.rotulo;
  textoEspacado(ctx, rotulo, PAD, PAD + 24, 8, est.ct);
  return PAD + 44;
}

/** Desenha o fio divisório e devolve onde o conteúdo de baixo começa. */
function fio(ctx: CanvasRenderingContext2D, est: Estilo, y: number): number {
  ctx.shadowBlur = 0;
  ctx.globalAlpha = 0.26; ctx.fillStyle = est.cor;
  ctx.fillRect(PAD, y, LARG - PAD * 2, 3);
  preparar(ctx, est);
  return y + DEPOIS_FIO;
}

/** Fundo de card, no tamanho exato da peça. Sem ele, o PNG é transparente. */
function desenharFundo(ctx: CanvasRenderingContext2D, altura: number, fundo: string): void {
  ctx.save();
  ctx.globalAlpha = 1;
  ctx.fillStyle = fundo;
  ctx.fill(caminhoArredondado(0, 0, LARG, altura, 40));
  ctx.restore();
}

/** Altura do que vem ANTES do conteúdo, com ou sem o bloco de identidade. */
function alturaCabeca(est: Estilo, titulo: string, autor?: string | null): number {
  if (!est.comTopo) return PAD + 44 + DEPOIS_FIO;
  return PAD + planoTopo(titulo, autor).alturaTopo + ATE_FIO + DEPOIS_FIO;
}

/** Onde cada coisa cai na peça do livro — medido antes de criar o canvas. */
function planoLivro(d: Extract<DadosOverlay, { tipo: 'livro' }>, est: Estilo) {
  // 206 até a linha de base da marca (154 da legenda + 52 de respiro) e 20
  // de descida da marca.
  return { altura: alturaCabeca(est, d.titulo, d.autor) + 206 + 20 + PAD };
}

/**
 * Peça do LIVRO — compacta, para colar sobre a foto do próprio livro.
 * A capa entra pequena, só como reforço: quem olha já está vendo o livro
 * na foto de baixo.
 */
function desenharLivro(
  ctx: CanvasRenderingContext2D, d: Extract<DadosOverlay, { tipo: 'livro' }>,
  est: Estilo, capa: HTMLImageElement | null,
): void {
  if (est.fundo) desenharFundo(ctx, planoLivro(d, est).altura, est.fundo);
  preparar(ctx, est);
  const rotulo = d.terminado ? 'TERMINEI' : 'LENDO AGORA';
  const y = est.comTopo
    ? fio(ctx, est, desenharTopo(ctx, est, capa, rotulo, d.titulo, d.autor))
    : fio(ctx, est, desenharRotuloSolto(ctx, est, rotulo));

  // ── os dois heróis ──
  const pct = d.totalPaginas > 0 ? Math.round((d.paginaAtual / d.totalPaginas) * 100) : 0;
  heroi(ctx, est, PAD, y, String(d.lidoHoje),
    d.metaDoLivro > 0 ? 'HOJE · META ' + d.metaDoLivro : 'HOJE', { selo: d.metaBatida });
  // Antes só o total aparecia, e ele sozinho não conta jornada nenhuma.
  heroi(ctx, est, PAD + 400, y, String(d.paginaAtual),
    d.terminado ? d.unidadeLonga.toUpperCase() + ' · FIM' : d.unidadeLonga.toUpperCase() + ' · ' + pct + '%',
    { fonte: F.heroi2, sufixo: '/ ' + d.totalPaginas });

  assinatura(ctx, est, PAD, y + 206);

  ctx.globalAlpha = 1;
  ctx.shadowBlur = 0;
}

/** Um número grande com a legenda embaixo. O par deles é o assunto da peça. */
function heroi(
  ctx: CanvasRenderingContext2D, est: Estilo, x: number, y: number,
  numero: string, legenda: string,
  opts?: { selo?: boolean; fonte?: string; sufixo?: string },
): void {
  // Tudo na MESMA linha de base: os três tamanhos convivem porque assentam
  // no mesmo chão.
  const base = y + 112;
  ctx.globalAlpha = 1; ctx.fillStyle = est.cor;
  ctx.font = opts?.fonte || F.heroi;
  escrever(ctx, numero, x, base, est.ct);
  let cursor = x + ctx.measureText(numero).width;

  if (opts?.sufixo) {
    ctx.globalAlpha = 0.5; ctx.fillStyle = est.cor;
    ctx.font = F.sufixo;
    escrever(ctx, opts.sufixo, cursor + 12, base, est.ct);
    cursor += 12 + ctx.measureText(opts.sufixo).width;
  }

  // Folga generosa antes do check: colado no número ele lia como dígito.
  if (opts?.selo) marcaCheck(ctx, cursor + 36, y + 74, 34, est.cor);

  ctx.globalAlpha = 0.66; ctx.fillStyle = est.cor;
  ctx.font = F.legenda;
  textoEspacado(ctx, legenda, x, y + 154, 6, est.ct);
}

/** As linhas do trecho, medidas com a fonte em que serão desenhadas. */
function linhasDaNota(d: Extract<DadosOverlay, { tipo: 'nota' }>): string[] {
  const ctx = medidor();
  ctx.font = F.nota;
  const texto = d.rotulo === 'GRIFO' ? '“' + d.texto + '”' : d.texto;
  return quebrar(ctx, texto, LARG - PAD * 2, 7);
}

function planoNota(d: Extract<DadosOverlay, { tipo: 'nota' }>, est: Estilo) {
  const linhas = linhasDaNota(d).length;
  return {
    altura: alturaCabeca(est, d.titulo, d.autor)
      + linhas * 62 + (d.pagina ? 62 : 16) + 44 + PAD,
  };
}

/**
 * Peça da NOTA — mesmo topo da peça de leitura; embaixo, no lugar dos
 * números, o que a pessoa guardou. O trecho é o herói aqui.
 */
function desenharNota(
  ctx: CanvasRenderingContext2D, d: Extract<DadosOverlay, { tipo: 'nota' }>,
  est: Estilo, capa: HTMLImageElement | null,
): void {
  if (est.fundo) desenharFundo(ctx, planoNota(d, est).altura, est.fundo);
  preparar(ctx, est);
  let y = est.comTopo
    ? fio(ctx, est, desenharTopo(ctx, est, capa, d.rotulo, d.titulo, d.autor))
    : fio(ctx, est, desenharRotuloSolto(ctx, est, d.rotulo));

  ctx.globalAlpha = 1; ctx.fillStyle = est.cor;
  ctx.font = F.nota;
  for (const linha of linhasDaNota(d)) { escrever(ctx, linha, PAD, y + 46, est.ct); y += 62; }

  if (d.pagina) {
    ctx.globalAlpha = 0.6; ctx.fillStyle = est.cor;
    ctx.font = F.legenda;
    textoEspacado(ctx, d.pagina.toUpperCase(), PAD, y + 30, 6, est.ct);
    y += 62;
  } else { y += 16; }

  assinatura(ctx, est, PAD, y + 30);

  ctx.globalAlpha = 1;
  ctx.shadowBlur = 0;
}

/** Altura da peça da meta — fixa, o conteúdo dela não varia de tamanho. */
function planoMeta() {
  return { altura: PAD + 36 + 130 + 40 + 62 + 44 + PAD };
}

/* ── Peça do PROTOCOLO ────────────────────────────────────────────────────
   O que se posta aqui não é o dia: é estar no meio de uma coisa longa. Por
   isso o herói é o número do dia e o denominador vem junto — "12 de 67" diz
   sozinho que existe um compromisso, o que "12" nunca diria.

   Ela carrega o invariante nº 7 do protocolo: contagem nunca vira promessa.
   Passado o marco, "de 67" é mentira aritmética ("dia 75 de 67") e o
   denominador SOME — exatamente como já some no card da Home. */

type DadosProtocolo = Extract<DadosOverlay, { tipo: 'protocolo' }>;

/** As quatro verdades possíveis do protocolo. O adesivo conta a que está
 *  acontecendo — quem começou hoje não posta a peça de quem terminou. */
function faseDoProtocolo(d: DadosProtocolo): 'comeco' | 'andando' | 'alem' | 'formado' {
  if (d.formado) return 'formado';
  if (d.dia <= 1) return 'comeco';
  return d.dia > d.marco ? 'alem' : 'andando';
}

function planoProtocolo(d: DadosProtocolo, est: Estilo) {
  const temLegenda = d.chama > 0 || d.diasLidos > 0;
  return {
    altura: alturaCabeca(est, d.titulo || '', d.autor)
      + 130 + 40 + (temLegenda ? 62 : 8) + 44 + PAD,
  };
}

function desenharProtocolo(
  ctx: CanvasRenderingContext2D, d: DadosProtocolo,
  est: Estilo, capa: HTMLImageElement | null,
): void {
  if (est.fundo) desenharFundo(ctx, planoProtocolo(d, est).altura, est.fundo);
  const tinta = preparar(ctx, est);
  const ct = est.ct;
  const fase = faseDoProtocolo(d);

  const ROTULO = {
    comeco:  'COMECEI O PROTOCOLO',
    andando: 'PROTOCOLO DO HÁBITO',
    alem:    'PROTOCOLO DO HÁBITO',
    formado: 'PROTOCOLO CUMPRIDO',
  } as const;

  // Formado mostra os dias LIDOS, não o número do dia: depois da formatura o
  // contador do programa perdeu a função, e o que ficou de pé foi a leitura.
  const numero = fase === 'formado' ? d.diasLidos : d.dia;

  let y = est.comTopo && d.titulo
    ? fio(ctx, est, desenharTopo(ctx, est, capa, ROTULO[fase], d.titulo, d.autor))
    : fio(ctx, est, desenharRotuloSolto(ctx, est, ROTULO[fase]));

  tinta(1);
  ctx.font = F.heroi;
  escrever(ctx, String(numero), PAD, y + 112, ct);
  const larg = ctx.measureText(String(numero)).width;

  // O "de 67" só existe enquanto for verdade.
  if (fase === 'comeco' || fase === 'andando') {
    tinta(0.55);
    ctx.font = '400 46px "Instrument Serif", Georgia, serif';
    escrever(ctx, 'de ' + d.marco, PAD + larg + 24, y + 112, ct);
  } else if (fase === 'formado') {
    marcaCheck(ctx, PAD + larg + 40, y + 74, 34, est.cor);
  }
  y += 130;

  tinta(0.72);
  ctx.font = 'italic 400 40px "Instrument Serif", Georgia, serif';
  escrever(ctx,
    fase === 'comeco'  ? 'dias · ' + d.estagio
    : fase === 'andando' ? 'dias · ' + d.estagio
    : fase === 'alem'    ? 'dias de protocolo'
    : 'dias lidos · ' + d.estagio,
    PAD, y + 30, ct,
  );
  y += 40;

  // A chama e os dias lidos dividem a linha. Sequência zerada não aparece —
  // é a regra nº 4 do protocolo, que a Home também respeita: nunca mostrar
  // zero, porque zero na tela lê como fracasso e não como começo.
  if (d.chama > 0 || d.diasLidos > 0) {
    let x = PAD;
    if (d.chama > 0) {
      tinta(0.85);
      ctx.fill(chamaPath(x + 13, y + 34, 17));
      tinta(0.78);
      ctx.font = F.legenda;
      const t = d.chama + (d.chama === 1 ? ' DIA ACESO' : ' DIAS ACESA');
      textoEspacado(ctx, t, x + 38, y + 42, 6, ct);
      x += 38 + ctx.measureText(t).width + t.length * 6 + 34;
    }
    if (d.diasLidos > 0 && fase !== 'formado') {
      tinta(0.62);
      ctx.font = F.legenda;
      textoEspacado(ctx, d.diasLidos + (d.diasLidos === 1 ? ' DIA LIDO' : ' DIAS LIDOS'), x, y + 42, 6, ct);
    }
    y += 62;
  } else { y += 8; }

  assinatura(ctx, est, PAD, y + 30);

  ctx.globalAlpha = 1;
  ctx.shadowBlur = 0;
}

/**
 * Peça da META do dia — a mais compacta das duas: um número e o que ele
 * significa. NÃO pinta o fundo, é isso que a mantém transparente.
 */
function desenharMeta(
  ctx: CanvasRenderingContext2D, d: Extract<DadosOverlay, { tipo: 'meta' }>, est: Estilo,
): void {
  if (est.fundo) desenharFundo(ctx, planoMeta().altura, est.fundo);
  const tinta = preparar(ctx, est);
  const ct = est.ct;

  // O dia tem três verdades diferentes e o adesivo conta a que está
  // acontecendo — quem bateu a meta não pode postar a peça de quem não leu.
  const faltam = Math.max(0, d.metaHoje - d.paginasHoje);
  const estado: 'batida' | 'andando' | 'zerado' =
    d.metaBatida ? 'batida' : d.paginasHoje > 0 ? 'andando' : 'zerado';
  const ROTULO = { batida: 'HÁBITO CUMPRIDO', andando: 'HOJE', zerado: 'FALTAM' } as const;
  // No estado zerado o número que importa é o que falta, não o zero.
  const numero = estado === 'zerado' ? faltam : d.paginasHoje;

  let y = PAD;
  tinta(0.68);
  ctx.font = F.rotulo;
  textoEspacado(ctx, ROTULO[estado], PAD, y + 22, 9, ct);
  y += 36;

  tinta(1);
  ctx.font = F.heroi;
  escrever(ctx, String(numero), PAD, y + 112, ct);
  const larg = ctx.measureText(String(numero)).width;
  if (estado === 'batida') {
    marcaCheck(ctx, PAD + larg + 40, y + 74, 34, est.cor);
  } else if (estado === 'andando') {
    tinta(0.55);
    ctx.font = '400 46px "Instrument Serif", Georgia, serif';
    escrever(ctx, '/ ' + d.metaHoje, PAD + larg + 24, y + 112, ct);
  }
  y += 130;

  tinta(0.72);
  ctx.font = 'italic 400 40px "Instrument Serif", Georgia, serif';
  escrever(ctx,
    estado === 'batida' ? d.unidadeLonga + ' · meta de ' + d.metaHoje
    : estado === 'zerado' ? d.unidadeLonga + ' para o hábito'
    : d.unidadeLonga,
    PAD, y + 30, ct,
  );
  y += 40;

  if (d.chama > 0) {
    tinta(0.85);
    ctx.fill(chamaPath(PAD + 13, y + 34, 17));
    tinta(0.78);
    ctx.font = F.legenda;
    textoEspacado(ctx, d.chama + (d.chama === 1 ? ' DIA ACESO' : ' DIAS ACESA'), PAD + 38, y + 42, 6, ct);
  }
  y += 62;

  assinatura(ctx, est, PAD, y + 30);

  ctx.globalAlpha = 1;
  ctx.shadowBlur = 0;
}

/** Canvas do TAMANHO DO ADESIVO — não do quadro do story. */
function gerarCanvas(d: DadosOverlay, est: Estilo, capa: HTMLImageElement | null): HTMLCanvasElement {
  const cv = document.createElement('canvas');
  cv.width = LARG;
  cv.height = Math.round(
    d.tipo === 'livro' ? planoLivro(d, est).altura
    : d.tipo === 'nota' ? planoNota(d, est).altura
    : d.tipo === 'protocolo' ? planoProtocolo(d, est).altura
    : planoMeta().altura,
  );
  const ctx = cv.getContext('2d');
  if (ctx) {
    if (d.tipo === 'livro') desenharLivro(ctx, d, est, capa);
    else if (d.tipo === 'nota') desenharNota(ctx, d, est, capa);
    else if (d.tipo === 'protocolo') desenharProtocolo(ctx, d, est, capa);
    else desenharMeta(ctx, d, est);
  }
  return cv;
}

// ─── Entrega do arquivo ───────────────────────────────────────────────────
// Mesmo caminho que o share-card já usa e que sabemos funcionar no app: no
// nativo, Filesystem grava e Share abre a folha; na web, Web Share API com
// File e, se o navegador não tiver, download.

async function blobDoCanvas(cv: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    cv.toBlob((b) => (b ? resolve(b) : reject(new Error('canvas vazio'))), 'image/png');
  });
}

async function entregar(blob: Blob, baixarDireto: boolean): Promise<void> {
  const nome = `forjamente-overlay-${Date.now()}.png`;

  if (isNativeApp()) {
    const fsMod: any = await import('@capacitor/filesystem').catch(() => null);
    const shareMod: any = await import('@capacitor/share').catch(() => null);
    if (!fsMod || !shareMod) throw new Error('plugins indisponíveis');

    const bytes = new Uint8Array(await blob.arrayBuffer());
    let bin = '';
    for (let i = 0; i < bytes.byteLength; i++) bin += String.fromCharCode(bytes[i]);
    const fsRes = await fsMod.Filesystem.writeFile({
      path: nome, data: btoa(bin), directory: fsMod.Directory.Cache,
    });
    // Só files — com url junto o iOS anexa dois itens na folha.
    await shareMod.Share.share({
      files: [fsRes.uri],
      dialogTitle: baixarDireto ? 'Salvar no dispositivo' : 'Compartilhar',
    });
    return;
  }

  const file = new File([blob], nome, { type: 'image/png' });
  if (!baixarDireto && (navigator as any).share && (navigator as any).canShare?.({ files: [file] })) {
    try {
      await (navigator as any).share({ files: [file], title: 'Forjamente' });
      return;
    } catch (e: any) {
      if (e?.name === 'AbortError') return;
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = nome;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ─── Modal ────────────────────────────────────────────────────────────────

type Fundo = 'xadrez' | 'escuro' | 'claro';

export const OverlayStoryModal: React.FC<{
  aberto: boolean;
  onFechar: () => void;
  dados: DadosOverlay;
}> = ({ aberto, onFechar, dados }) => {
  const [cor, setCor] = useState('#FFFFFF');
  // Contorno desligado por padrão: sobre foto calma ele só engorda a letra.
  // Quem posta sobre foto cheia liga e resolve.
  const [ctLarg, setCtLarg] = useState(0);
  const [ctCor, setCtCor] = useState('#161210');
  // Fundo de card: mais uma opção, não uma troca de rumo. `null` mantém o
  // adesivo transparente, que segue sendo o padrão.
  const [fundoCard, setFundoCard] = useState<string | null>(null);
  const [comTopo, setComTopo] = useState(true);
  const [fundo, setFundo] = useState<Fundo>('xadrez');
  const [previa, setPrevia] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState<null | 'share' | 'save'>(null);
  const [erro, setErro] = useState<string | null>(null);
  const fontesOk = useRef(false);
  const capaRef = useRef<HTMLImageElement | null>(null);
  const capaDe = useRef<string | null>(null);

  const capaUrl = dados.tipo === 'meta' ? null : (dados.capaUrl || null);
  // A peça da meta nunca tem topo. A do protocolo só tem quando existe livro:
  // quem começou o programa e ainda não escolheu o que ler ganharia um bloco
  // de capa vazio, e o botão "com capa e título" prometeria o que não há.
  const temTopo = dados.tipo === 'livro' || dados.tipo === 'nota'
    || (dados.tipo === 'protocolo' && !!dados.titulo);

  const estilo: Estilo = useMemo(() => ({
    cor, ct: { cor: ctCor, larg: ctLarg }, fundo: fundoCard, comTopo: temTopo && comTopo,
  }), [cor, ctCor, ctLarg, fundoCard, temTopo, comTopo]);

  // Redesenha a cada troca de cor. É barato (um canvas 1080×1920) e mantém a
  // prévia sendo exatamente o arquivo que vai sair — não uma aproximação.
  // A capa é carregada UMA vez por livro e fica no ref: trocar de cor não
  // deve pagar o download de novo.
  useEffect(() => {
    if (!aberto) return;
    let vivo = true;
    (async () => {
      if (!fontesOk.current) { await garantirFontes(); await garantirLogo(); fontesOk.current = true; }
      if (capaUrl && capaDe.current !== capaUrl) {
        capaRef.current = await carregarCapa(capaUrl);
        capaDe.current = capaUrl;
      }
      if (!capaUrl) { capaRef.current = null; capaDe.current = null; }
      if (!vivo) return;
      setPrevia(gerarCanvas(dados, estilo, capaRef.current).toDataURL('image/png'));
    })();
    return () => { vivo = false; };
  }, [aberto, dados, capaUrl, estilo]);

  const agir = useCallback(async (modo: 'share' | 'save') => {
    if (ocupado) return;
    setOcupado(modo); setErro(null);
    try {
      await garantirFontes(); await garantirLogo();
      await entregar(await blobDoCanvas(gerarCanvas(dados, estilo, capaRef.current)), modo === 'save');
    } catch (e: any) {
      setErro(e?.message || 'Não consegui gerar a imagem.');
    } finally {
      setOcupado(null);
    }
  }, [ocupado, dados, estilo]);

  if (!aberto) return null;

  const fundos: Record<Fundo, React.CSSProperties> = {
    // Xadrez = a convenção universal de "aqui não tem nada". É o que prova
    // que o PNG é mesmo transparente.
    xadrez: {
      backgroundImage:
        'repeating-conic-gradient(#d8d2c8 0% 25%, #f0ebe2 0% 50%)',
      backgroundSize: '22px 22px',
    },
    escuro: { background: 'linear-gradient(160deg, #2a2320, #0d0b0a)' },
    claro:  { background: 'linear-gradient(160deg, #efe9dd, #cfc6b6)' },
  };

  // PORTAL, não render inline: o modal nasce dentro do card, e um card com
  // overflow/z-index próprio cria contexto de empilhamento — o `zIndex: 4000`
  // valeria só ali dentro, e a barra de navegação passava por cima. No body
  // ele disputa com a raiz, que é onde ele precisa ganhar.
  return createPortal(
    <>
      <div onClick={onFechar} style={{
        position: 'fixed', inset: 0, background: 'rgba(10,8,7,0.86)',
        backdropFilter: 'blur(3px)', zIndex: 4000,
      }}/>
      <div style={{
        position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 4001,
        background: 'var(--paper-2)', borderRadius: '24px 24px 0 0',
        padding: '18px 20px calc(20px + env(safe-area-inset-bottom))',
        maxHeight: '92vh', overflowY: 'auto',
        borderTop: '1px solid var(--line)',
      }}>
        <div style={{ width: 36, height: 4, borderRadius: 2, background: 'var(--ink-4)', opacity: 0.3, margin: '0 auto 16px' }}/>

        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 4 }}>
          <div className="serif" style={{ fontSize: 21 }}>Adesivo para Stories</div>
          <button onClick={onFechar} className="mono" style={{
            fontSize: 11, letterSpacing: '0.1em', color: 'var(--ink-3)', background: 'none', border: 'none',
          }}>FECHAR</button>
        </div>
        {/* A frase acompanha a escolha: com fundo de card ela deixaria de ser
            verdade, e legenda que mente é pior que legenda nenhuma. */}
        <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginBottom: 16, lineHeight: 1.45 }}>
          {fundoCard
            ? <>Card fechado — ele entra por cima da <i>sua</i> foto.</>
            : <>Fundo transparente — ele entra por cima da <i>sua</i> foto.</>}
        </div>

        {/* Prévia. O quadro é 9:16 igual ao Stories, então o que se vê aqui é
            o enquadramento real, não uma amostra. */}
        <div style={{
          borderRadius: 16, overflow: 'hidden', border: '1px solid var(--line)',
          aspectRatio: '9 / 16', maxHeight: '46vh', margin: '0 auto 12px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          ...fundos[fundo],
        }}>
          {/* 78% da largura é mais ou menos o tamanho em que um adesivo cai
              num story. Mostrar o PNG esticado no quadro inteiro mentiria
              sobre a proporção — e proporção é o assunto aqui. */}
          {previa
            ? <img src={previa} alt="Prévia do adesivo" style={{ width: '78%', height: 'auto', display: 'block' }}/>
            : <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>montando…</span>}
        </div>

        {/* Testar em fundo claro e escuro é o que faz a escolha de cor deixar
            de ser chute. */}
        <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginBottom: 18 }}>
          {(['xadrez', 'escuro', 'claro'] as Fundo[]).map((f) => (
            <button key={f} onClick={() => setFundo(f)} className="mono" style={{
              fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase',
              padding: '6px 12px', borderRadius: 999, border: '1px solid var(--line)',
              background: fundo === f ? 'var(--ember-soft)' : 'transparent',
              color: fundo === f ? 'var(--ember)' : 'var(--ink-3)',
            }}>{f}</button>
          ))}
        </div>

        <div className="mono" style={{
          fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase',
          color: 'var(--ink-3)', marginBottom: 10,
        }}>cor do texto</div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 20 }}>
          {CORES.map((c) => (
            <button key={c.hex} onClick={() => setCor(c.hex)} title={c.nome} style={{
              width: 38, height: 38, borderRadius: '50%', background: c.hex,
              border: cor.toUpperCase() === c.hex ? '2px solid var(--ember)' : '1px solid var(--line)',
              boxShadow: cor.toUpperCase() === c.hex ? '0 0 0 3px var(--ember-soft)' : 'none',
            }}/>
          ))}
          {/* Qualquer outra cor — o leitor casa com a foto dele. */}
          <label style={{
            position: 'relative',
            width: 38, height: 38, borderRadius: '50%', border: '1px dashed var(--ink-4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
          }}>
            <Icon name="plus" size={15} color="var(--ink-3)"/>
            <input type="color" value={cor} onChange={(e) => setCor(e.target.value)}
                   style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}/>
          </label>
        </div>

        {/* Fundo de card — não cobre o story, só a área do conteúdo. É o
            mesmo desenho, com um retângulo por trás. */}
        <div className="mono" style={{
          fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase',
          color: 'var(--ink-3)', marginBottom: 10,
        }}>fundo do card</div>

        <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: 20 }}>
          {FUNDOS.map((f) => (
            <button key={f.nome} onClick={() => {
              setFundoCard(f.hex);
              // Escolher fundo escolhe também uma cor de texto que se lê nele.
              // Sem isso, branco sobre branco sumia e a opção parecia quebrada.
              if (f.texto) setCor(f.texto);
            }} className="mono" style={{
              fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase',
              padding: '7px 13px', borderRadius: 999, border: '1px solid var(--line)',
              background: fundoCard === f.hex ? 'var(--ember-soft)' : 'transparent',
              color: fundoCard === f.hex ? 'var(--ember)' : 'var(--ink-3)', cursor: 'pointer',
            }}>{f.nome}</button>
          ))}
        </div>

        {/* Capa e título são opcionais: às vezes o que importa é só o trecho. */}
        {temTopo && (
          <>
            <div className="mono" style={{
              fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase',
              color: 'var(--ink-3)', marginBottom: 10,
            }}>capa e título</div>
            <div style={{ display: 'flex', gap: 7, marginBottom: 20 }}>
              {[{ r: 'Mostrar', v: true }, { r: 'Só o conteúdo', v: false }].map((o) => (
                <button key={String(o.v)} onClick={() => setComTopo(o.v)} className="mono" style={{
                  fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase',
                  padding: '7px 13px', borderRadius: 999, border: '1px solid var(--line)',
                  background: comTopo === o.v ? 'var(--ember-soft)' : 'transparent',
                  color: comTopo === o.v ? 'var(--ember)' : 'var(--ink-3)', cursor: 'pointer',
                }}>{o.r}</button>
              ))}
            </div>
          </>
        )}

        {/* Contorno — o que salva a peça sobre foto cheia, onde nenhuma cor
            de texto sozinha se separa do fundo. */}
        <div className="mono" style={{
          fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase',
          color: 'var(--ink-3)', marginBottom: 10,
        }}>contorno</div>

        <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: ctLarg > 0 ? 12 : 20 }}>
          {/* Fino é um fio de cabelo — 0,8 vira 1,6px de traço num canvas de
              900. Serve pra descolar a letra do fundo sem engordá-la. */}
          {[{ r: 'Sem', v: 0 }, { r: 'Fino', v: 0.8 }, { r: 'Médio', v: 2.5 }, { r: 'Grosso', v: 5 }].map((o) => (
            <button key={o.v} onClick={() => setCtLarg(o.v)} className="mono" style={{
              fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase',
              padding: '7px 13px', borderRadius: 999, border: '1px solid var(--line)',
              background: ctLarg === o.v ? 'var(--ember-soft)' : 'transparent',
              color: ctLarg === o.v ? 'var(--ember)' : 'var(--ink-3)', cursor: 'pointer',
            }}>{o.r}</button>
          ))}
        </div>

        {ctLarg > 0 && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 20 }}>
            {CORES.map((c) => (
              <button key={c.hex} onClick={() => setCtCor(c.hex)} title={c.nome} style={{
                width: 32, height: 32, borderRadius: '50%', background: c.hex, cursor: 'pointer',
                border: ctCor.toUpperCase() === c.hex ? '2px solid var(--ember)' : '1px solid var(--line)',
                boxShadow: ctCor.toUpperCase() === c.hex ? '0 0 0 3px var(--ember-soft)' : 'none',
              }}/>
            ))}
            <label style={{
              position: 'relative',
              width: 32, height: 32, borderRadius: '50%', border: '1px dashed var(--ink-4)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
            }}>
              <Icon name="plus" size={13} color="var(--ink-3)"/>
              <input type="color" value={ctCor} onChange={(e) => setCtCor(e.target.value)}
                     style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}/>
            </label>
          </div>
        )}

        {erro && (
          <div style={{ fontSize: 12, color: 'var(--ember)', marginBottom: 12 }}>{erro}</div>
        )}

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => agir('save')} disabled={!!ocupado} className="btn btn-ghost"
                  style={{ flex: 1, padding: 14, opacity: ocupado ? 0.5 : 1 }}>
            {ocupado === 'save' ? 'Salvando…' : 'Salvar PNG'}
          </button>
          <button onClick={() => agir('share')} disabled={!!ocupado} className="btn btn-ember"
                  style={{ flex: 2, padding: 14, opacity: ocupado ? 0.7 : 1 }}>
            {ocupado === 'share' ? 'Gerando…' : 'Compartilhar'}
          </button>
        </div>
      </div>
    </>,
    document.body,
  );
};

/**
 * Botãozinho de canto — quem abre o modal.
 *
 * `top`/`right`/`caixa` existem porque ele mora em cartões com âncoras
 * diferentes: sozinho no card de metas, e ombro a ombro com o "..." no card
 * de livro (onde precisa do mesmo tamanho e da mesma linha).
 */
export const BotaoOverlayStory: React.FC<{
  onClick: () => void;
  top?: number;
  right?: number;
  caixa?: number;
  fundo?: string;
}> = ({ onClick, top = 14, right = 14, caixa = 34, fundo = 'var(--paper-3)' }) => (
  <button
    onClick={(e) => { e.stopPropagation(); onClick(); }}
    title="Adesivo para Stories"
    aria-label="Adesivo para Stories"
    style={{
      position: 'absolute', top, right, zIndex: 4,
      width: caixa, height: caixa, borderRadius: 999,
      background: fundo, border: 'none', cursor: 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
    <Icon name="share" size={Math.round(caixa * 0.45)} color="var(--ink-3)"/>
  </button>
);
